import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { pushSupported, isPushEnabled, enablePush, disablePush, sendTestPush } from "@/lib/push";
import { BellRing, BellOff, Download, Loader2, Check, Smartphone } from "lucide-react";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function PushSetup() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installEvt, setInstallEvt] = useState(window.__deferredPWAPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  const supported = pushSupported();

  useEffect(() => {
    isPushEnabled().then(setEnabled);
    const onProm = () => setInstallEvt(window.__deferredPWAPrompt || null);
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); };
    window.addEventListener("pwa-installable", onProm);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onProm);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success("Notificaciones desactivadas");
      } else {
        await enablePush();
        setEnabled(true);
        await sendTestPush();
        toast.success("¡Notificaciones activadas!");
      }
    } catch (e) {
      toast.error(e.message || "No se pudo activar");
    } finally {
      setBusy(false);
    }
  };

  const install = async () => {
    if (!installEvt) {
      toast.info("Usa el menú del navegador → 'Instalar app' / 'Agregar a pantalla de inicio'");
      return;
    }
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    if (outcome === "accepted") { setInstalled(true); toast.success("App instalada"); }
    window.__deferredPWAPrompt = null;
    setInstallEvt(null);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="push-setup">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
          {enabled ? <BellRing className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-sm font-bold text-slate-900">Notificaciones push</h3>
          <p className="text-xs text-slate-500">
            {enabled
              ? "Activadas. Recibirás avisos aunque la app esté cerrada."
              : supported
              ? "Recibe avisos de tus tareas en este dispositivo, incluso con la app cerrada."
              : "Este navegador no soporta notificaciones push. Instala la app para habilitarlas."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {supported && (
              <Button onClick={toggle} disabled={busy} size="sm"
                data-testid="toggle-push-btn"
                className={enabled ? "rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200" : "rounded-full bg-slate-900 hover:bg-slate-800"}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : enabled ? <>Desactivar</> : <><BellRing className="mr-1 h-4 w-4" /> Activar notificaciones</>}
              </Button>
            )}
            {enabled && (
              <Button onClick={() => sendTestPush().then(() => toast.success("Enviada"))} variant="outline" size="sm" className="rounded-full" data-testid="test-push-btn">
                Probar
              </Button>
            )}
          </div>
        </div>
      </div>

      {!installed && (
        <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm font-bold text-slate-900">Instalar aplicación</h3>
            <p className="text-xs text-slate-500">Instálala en tu celular para usarla como una app nativa.</p>
          </div>
          <Button onClick={install} size="sm" variant="outline" className="rounded-full" data-testid="install-pwa-btn">
            <Download className="mr-1 h-4 w-4" /> Instalar
          </Button>
        </div>
      )}
      {installed && (
        <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4 text-xs font-semibold text-emerald-600">
          <Check className="h-4 w-4" /> App instalada en este dispositivo
        </div>
      )}
    </div>
  );
}
